import { useState, useEffect } from 'react';
import { LuUser, LuClock, LuTrash2, LuPaperclip } from 'react-icons/lu';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { isManager } from '../../utils/roleCheck';
import { toast } from '../../hooks/use-toast';
import MentionText from '../common/MentionText';
import AttachmentList from '../FaultReports/AttachmentList';

export default function ReplySection({ topicId }) {
  const { user } = useAuth();
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attachmentsByReply, setAttachmentsByReply] = useState({});

  useEffect(() => {
    loadReplies();
  }, [topicId]);

  const loadReplies = async () => {
    setLoading(true);
    try {
      const { data, error } = await window.ezsite.apis.tablePage(70653, {
        PageNo: 1,
        PageSize: 100,
        Filters: [{ name: 'topic_id', op: 'Equal', value: Number(topicId) }],
        OrderByField: 'created_at',
        IsAsc: true
      });

      if (error) throw error;
      const replyList = data?.List || [];

      // Load author names
      const repliesWithAuthors = await Promise.all(
        replyList.map(async (reply) => {
          try {
            const { data: userData } = await window.ezsite.apis.tablePage(70648, {
              PageNo: 1,
              PageSize: 1,
              Filters: [{ name: 'id', op: 'Equal', value: reply.author_id }]
            });
            return { ...reply, authorName: userData?.List?.[0]?.name || 'Unknown' };
          } catch {
            return { ...reply, authorName: 'Unknown' };
          }
        })
      );

      setReplies(repliesWithAuthors);

      // Load attachments for all replies
      if (repliesWithAuthors.length > 0) {
        await loadAttachments(repliesWithAuthors.map((r) => r.id));
      }
    } catch (error) {
      console.error('Failed to load replies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAttachments = async (replyIds) => {
    try {
      const attachmentsMap = {};

      // Load attachments for each reply
      await Promise.all(
        replyIds.map(async (replyId) => {
          const { data, error } = await window.ezsite.apis.tablePage(71361, {
            PageNo: 1,
            PageSize: 50,
            Filters: [{ name: 'reply_id', op: 'Equal', value: Number(replyId) }],
            OrderByField: 'uploaded_at',
            IsAsc: false
          });

          if (!error && data?.List) {
            attachmentsMap[replyId] = data.List;
          }
        })
      );

      setAttachmentsByReply(attachmentsMap);
    } catch (error) {
      console.error('Failed to load attachments:', error);
    }
  };

  const handleDelete = async (replyId) => {
    if (!confirm('Are you sure you want to delete this reply?')) return;

    try {
      // Delete attachments first
      const attachments = attachmentsByReply[replyId] || [];
      for (const attachment of attachments) {
        await window.ezsite.apis.tableDelete(71361, { ID: attachment.id });
      }

      // Delete reply
      const { error } = await window.ezsite.apis.tableDelete(70653, { ID: replyId });
      if (error) throw error;

      toast({ title: 'Success', description: 'Reply deleted successfully' });
      loadReplies();

      // Update reply count
      const { data: topicData } = await window.ezsite.apis.tablePage(70652, {
        PageNo: 1,
        PageSize: 1,
        Filters: [{ name: 'id', op: 'Equal', value: Number(topicId) }]
      });

      if (topicData?.List?.[0]) {
        await window.ezsite.apis.tableUpdate(70652, {
          id: Number(topicId),
          reply_count: Math.max(0, (topicData.List[0].reply_count || 0) - 1)
        });
      }
    } catch (error) {
      console.error('Failed to delete reply:', error);
      toast({ title: 'Error', description: 'Failed to delete reply', variant: 'destructive' });
    }
  };

  const handleDeleteAttachment = async (attachmentId, replyId) => {
    if (!confirm('Are you sure you want to delete this attachment?')) return;

    try {
      const { error } = await window.ezsite.apis.tableDelete(71361, { ID: attachmentId });
      if (error) throw error;

      toast({ title: 'Success', description: 'Attachment deleted successfully' });

      // Reload attachments for this reply
      await loadAttachments([replyId]);
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      toast({ title: 'Error', description: 'Failed to delete attachment', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center text-gray-500">
        Loading replies...
      </div>);

  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900">
          Replies ({replies.length})
        </h2>
      </div>
      
      {replies.length === 0 ?
      <div className="p-8 text-center text-gray-500">
          No replies yet. Be the first to reply!
        </div> :

      <div className="divide-y divide-gray-200">
          {replies.map((reply) => {
          const canDelete = isManager(user?.role) || reply.author_id === user?.id;
          const attachments = attachmentsByReply[reply.id] || [];

          return (
            <div key={reply.id} className="p-6">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                    {reply.authorName.charAt(0).toUpperCase()}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3 text-sm text-gray-600">
                        <span className="flex items-center gap-1 font-medium text-gray-900">
                          <LuUser className="w-4 h-4" /> {reply.authorName}
                        </span>
                        <span className="flex items-center gap-1">
                          <LuClock className="w-4 h-4" /> {format(new Date(reply.created_at), 'MMM d, yyyy h:mm a')}
                        </span>
                        {attachments.length > 0 &&
                      <span className="flex items-center gap-1 text-blue-600">
                            <LuPaperclip className="w-4 h-4" />
                            {attachments.length}
                          </span>
                      }
                      </div>
                      {canDelete &&
                    <button
                      onClick={() => handleDelete(reply.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      title="Delete reply">
                          <LuTrash2 className="w-4 h-4" />
                        </button>
                    }
                    </div>
                    
                    <div className="text-gray-700 whitespace-pre-wrap mb-4">
                      <MentionText text={reply.body} />
                    </div>

                    {/* Attachments */}
                    {attachments.length > 0 &&
                  <div className="mt-4">
                        <AttachmentList attachments={attachments} />
                        {canDelete &&
                    <div className="mt-3 flex flex-wrap gap-2">
                            {attachments.map((attachment) =>
                      <button
                        key={attachment.id}
                        onClick={() => handleDeleteAttachment(attachment.id, reply.id)}
                        className="text-xs px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                        title="Delete attachment">
                                Delete {attachment.file_name}
                              </button>
                      )}
                          </div>
                    }
                      </div>
                  }
                  </div>
                </div>
              </div>);

        })}
        </div>
      }
    </div>);

}